<template>
  <div>
    <!-- price table -->
    <section class="mt-16 hidden md:block">
      <!-- row -->
      <div class="grid grid-cols-1 sm:grid-cols-5 gap-4">
        <div></div>

        <!-- col -->
        <div
          v-for="(col, index) in headers.title"
          :key="index"
          class="bg-gray-100 p-2 rounded"
        >
          <div class="font-semibold text-lg">{{ headers.title[index] }}</div>
          <div class="text-xs mt-2">
            {{ headers.description[index] }}
          </div>
        </div>
      </div>

      <!-- row -->
      <div class="grid grid-cols-1 sm:grid-cols-5 gap-4">
        <div></div>

        <div
          class="flex flex-col justify-between bg-gray-100 p-2"
          v-for="(col, index) in headers.price"
          :key="index"
        >
          <div
            v-if="typeof headers.price[index] == 'number'"
            class="mt-4 font-semibold text-4xl"
          >
            ${{ headers.price[index]
            }}<span class="text-sm font-normal text-gray-600">/month</span>
          </div>
          <div v-else class="mt-4 font-semibold text-xl mt-8">
            {{ headers.price[index] }}
          </div>

          <div
            v-if="buttonsVisible"
            @click="getStarted(headers.title[index])"
            class="btn btn-secondary rounded-lg text-center mt-6 mb-4"
          >
            {{ headers.button[index] }}
          </div>
        </div>
        <!--   -->
      </div>

      <!-- row -->
      <div
        class="grid grid-cols-1 sm:grid-cols-5 gap-4 mt-2 border-t-2 border-gray-200 pt-4"
        v-for="(row, index) in dataset"
        :key="index"
      >
        <div class="text-sm">{{ row.label }}</div>

        <div
          v-for="(col, cIndex) in row.values"
          :key="cIndex"
          class="text-center font-semibold text-lg"
        >
          {{ col }}
        </div>
      </div>
    </section>

    <!-- price table mobile -->
    <section class="mt-16 block md:hidden">
      <!-- tab -->
      <div class="mb-16" v-for="(tab, index) in headers.title" :key="index">
        <div class="font-semibold text-3xl">{{ headers.title[index] }}</div>
        <div class="text-gray-800">
          {{ headers.description[index] }}
        </div>

        <div
          v-if="typeof headers.price[index] == 'number'"
          class="mt-4 font-semibold text-4xl"
        >
          ${{ headers.price[index] }}
          <span class="text-sm font-normal text-gray-600">/month</span>
        </div>
        <div v-else class="mt-4 font-semibold text-2xl">
          {{ headers.price[index] }}
        </div>

        <ul class="mt-6">
          <li
            class="flex justify-between mt-2"
            v-for="(item, index2) in dataset"
            :key="index2"
          >
            <span>{{ item.label }}</span
            ><span class="font-semibold text-lg">{{ item.values[index] }}</span>
          </li>
        </ul>
        <div
          v-if="buttonsVisible"
          class="btn btn-secondary rounded-lg text-center mt-8"
          @click="getStarted(headers.title[index])"
        >
          {{ headers.button[index] }}
        </div>
      </div>
    </section>

    <Modal
      :closeIcon="true"
      :withoutFooter="true"
      v-if="paymentModalVisible"
      @close="selectedPlan = null"
    >
      <div slot="body">
        <p class="font-semibold text-2xl">
          Your Selected Seat Plan: {{ selectedPlan }}
        </p>
        <p>{{ headers.description[selectedPlanIndex] }}</p>
        <p class="font-semibold text-xl mt-4">
          Total: ${{ headers.price[selectedPlanIndex] }}
        </p>

        <div class="mt-16 font-semibold">Your Payment Details</div>

        <!-- show payment options -->
        <DefaultPaymentSource @selected="cardSelected = true" class="mt-4" />

        <StyledButton @click="buyLicense" type="primary" class="mt-8">Confirm</StyledButton>
      </div>
    </Modal>
  </div>
</template>

<script>
import PaymentSources from "@/components/common/payments/PaymentSourcesComponent";
import ApiV1 from "@/api/api_v1.0";
import Modal from "@/components/common/Modal";
import DefaultPaymentSource from "@/components/common/payments/DefaultPaymentSource";
import StyledButton from "@/components/common/controls/StyledButton";

export default {
  name: "OrderPriceTableComponent",
  components: {
    PaymentSources,
    Modal,
    DefaultPaymentSource,
    StyledButton
  },

  props: {
    headersProp: {
      required: false,
      default: false
    },
    datasetProp: {
      required: false,
      default: false
    }
  },

  data() {
    return {
      selectedPlan: null,
      dataset: null,
      headers: null,
      cardSelected: false
    };
  },

  created() {
    if (this.headersProp) {
      this.headers = this.headersProp;
    } else {
      this.headers = {
        title: ["Free", "Premium", "Team", "Business"],
        description: [
          "For individuals and small organizations trying out BuildaBox.co for an unlimited period. Limited to events of 25 or fewer attendees.",
          "For professionals and organizations that want to use BuildaBox.co without integrations.",
          "For professionals and organizations that want to use BuildaBox.co in connection with their CRM, ERP and other marketing technology.",
          "For medium and large sized organizations that have advanced needs."
        ],
        price: [0, 20, 40, "Custom Pricing"],
        button: ["Activate", "Get Started", "Get Started", "Contact Us"]
      };
    }

    if (this.datasetProp) {
      this.dataset = this.datasetProp;
    } else {
      this.dataset = [
        {
          label: "Attendee Limits",
          values: [25, 100, 200, "Unlimited"]
        },
        {
          label: "Active Events",
          values: [15, 25, 50, "Unlimited"]
        },
        {
          label: "Monthly Message Limit",
          values: [100, 500, 1000, "Unlimited"]
        },
        {
          label: "All Event Types",
          values: ["*", "*", "*", "*"]
        },
        {
          label: "BuildaBox.co App Usage",
          values: ["*", "*", "*", "*"]
        },
        {
          label: "Collaborator and Account Tools",
          values: ["", "*", "*", "*"]
        },
        {
          label: "Salesforce Integration",
          values: ["", "", "*", "*"]
        },
        {
          label: "Mailchimp Integration",
          values: ["", "", "", "*"]
        },
        {
          label: "Constant Contact Integration",
          values: ["", "", "", "*"]
        },
        {
          label: "Zoho Integrations",
          values: ["", "", "", "*"]
        },
        {
          label: "Custom Integrations",
          values: ["", "", "", "*"]
        },
        {
          label: "Dedicated Project Support",
          values: ["", "", "", "*"]
        }
      ];
    }
  },

  methods: {
    async buyLicense(card) {
      await ApiV1.buyLicense({
        license: this.selectedPlan
      });

      this.showSuccess();

      this.$root.$emit("closeLicenseModal");

      this.selectedPlan = null;
    },
    async setFree() {
      await ApiV1.setFreeLicense();

      this.$root.$emit("closeLicenseModal");

      this.selectedPlan = null;
    },
    showSuccess() {
      this.$store.dispatch("setSuccessMessage", "License sucessfully added!");
    },

    contactus(type) {
      this.$router.push("/get-in-touch");
    },

    getStarted(type) {
      switch (type) {
        case "Business":
          this.contactus(type);
          break;

        case "Team":
        case "Premium":
          this.selectedPlan = type;

          break;

        case "Free":
          if (!this.isLoggedIn) {
            this.$router.push("/segmentation");
            return;
          }

          this.selectedPlan = type;
          this.setFree();
          this.changeActivation();
          break;
      }
    },

    async changeActivation() {
      await ApiV1.changeOrderActiveState(this.order.uuid, true);

      this.showGlobalSuccessMessage("The project is activated");

      let order = this.order;
      order.active = true;
      this.$store.commit("setCurrentEditingOrder", order);
    }
  },

  computed: {
    paymentModalVisible() {
      return ["Premium", "Team"].includes(this.selectedPlan);
    },
    selectedPlanIndex() {
      return this.headers.title.indexOf(this.selectedPlan);
    },
    buttonsVisible() {
      return true;
    },
    order() {
      return this.$store.state.currentEditingOrder;
    }
  }
};
</script>

<style></style>
